import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FETCHER from "./_fetcher";
import {
  RedemptionConfig,
  RedemptionCatalogResponse,
  RedemptionValidationRequest,
  RedemptionValidationResponse,
  ApplyRedemptionRequest,
  ApplyRedemptionResponse,
  // CartRedemptionValidationRequest,
  // CartRedemptionValidationResponse,
} from "types/redemption";

// Get Redemption Configuration
export const getRedemptionConfig = async (): Promise<RedemptionConfig> => {
  try {
    const result = await FETCHER.get<RedemptionConfig>(
      "/user/redemption/config"
    );
    return result.data;
  } catch (e) {
    console.error("Failed to get redemption config:", e);
    throw e;
  }
};

export const useGetRedemptionConfig = (enabled: boolean = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["getRedemptionConfig"],
    queryFn: getRedemptionConfig,
    enabled,
    retry: 2,
  });

  return {
    redemptionConfig: data,
    isLoading,
    error,
  };
};

// Get Redemption Catalog
export const getRedemptionCatalog =
  async (): Promise<RedemptionCatalogResponse> => {
    try {
      const result = await FETCHER.get<RedemptionCatalogResponse>(
        "/user/redemption/catalog"
      );
      return result.data;
    } catch (e) {
      console.error("Failed to get redemption catalog:", e);
      throw e;
    }
  };

export const useGetRedemptionCatalog = (enabled: boolean = true) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["getRedemptionCatalog"],
    queryFn: getRedemptionCatalog,
    enabled,
    retry: 2,
  });

  return {
    catalogData: data,
    redemptionItems: data?.items || [],
    userCapsulePoints: data?.userCapsulePoints || 0,
    redemptionConfig: data?.redemptionConfig,
    isLoading,
    error,
    refetch,
  };
};

// Validate Redemption
export const validateRedemption = async (
  request: RedemptionValidationRequest
): Promise<RedemptionValidationResponse> => {
  try {
    const result = await FETCHER.post<RedemptionValidationResponse>(
      "/user/redemption/validate",
      request
    );
    return result.data;
  } catch (e) {
    console.error("Failed to validate redemption:", e);
    throw e;
  }
};

export const useValidateRedemption = () => {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: validateRedemption,
    onError: (error) => {
      console.error("Redemption validation failed:", error);
    },
  });

  return {
    validateRedemptionAsync: mutateAsync,
    isValidating: isPending,
    validationError: error,
  };
};

// Cart-Level Validation
// export const validateCartRedemptions = async (
//   request: CartRedemptionValidationRequest
// ): Promise<CartRedemptionValidationResponse> => {
//   try {
//     const result = await FETCHER.post<CartRedemptionValidationResponse>(
//       "/user/redemption/validate-cart",
//       request
//     );
//     return result.data;
//   } catch (e) {
//     console.error("Failed to validate cart redemptions:", e);
//     throw e;
//   }
// };

// export const useValidateCartRedemptions = () => {
//   const { mutateAsync, isPending, error } = useMutation({
//     mutationFn: validateCartRedemptions,
//     onError: (error) => {
//       console.error("Cart redemption validation failed:", error);
//     },
//   });

//   return {
//     validateCartRedemptionsAsync: mutateAsync,
//     isValidatingCart: isPending,
//     cartValidationError: error,
//   };
// };

// Apply Redemption
export const applyRedemption = async (
  request: ApplyRedemptionRequest
): Promise<ApplyRedemptionResponse> => {
  try {
    const result = await FETCHER.post<ApplyRedemptionResponse>(
      "/user/redemption/apply",
      request
    );
    return result.data;
  } catch (e) {
    console.error("Failed to apply redemption:", e);
    throw e;
  }
};

export const useApplyRedemption = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: applyRedemption,
    onSuccess: () => {
      // Invalidate relevant queries after successful redemption
      queryClient.invalidateQueries({ queryKey: ["getRedemptionCatalog"] });
      queryClient.invalidateQueries({ queryKey: ["getUserMe"] });
      queryClient.invalidateQueries({ queryKey: ["getCart"] });
    },
    onError: (error) => {
      console.error("Redemption application failed:", error);
    },
  });

  return {
    applyRedemptionAsync: mutateAsync,
    isApplying: isPending,
    applicationError: error,
  };
};

// Helper function to handle redemption errors
export const handleRedemptionError = (error: any): string => {
  // API validation errors
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Network errors
  if (error.code === "NETWORK_ERROR") {
    return "Network connection failed. Please check your internet connection.";
  }

  // Authentication errors
  if (error.response?.status === 401) {
    return "Session expired. Please log in again.";
  }

  // Server errors
  if (error.response?.status >= 500) {
    return "Server error. Please try again later.";
  }

  return "An unexpected error occurred. Please try again.";
};
